import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/AppShell";
import { useAuth } from "@/context/AuthContext";
import { TenantsAPI, InstitutionsAPI, YearsAPI, ClassesAPI } from "@/lib/sprint2-api";
import { AdmissionsAPI, APPLICATION_STATUSES } from "@/lib/sprint4-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Search, Inbox, ExternalLink, FileText, ArrowUpRight, Copy, Check } from "lucide-react";

const STATUS_META = {
  applied:     { label: "Applied",      color: "bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/20" },
  screening:   { label: "Screening",    color: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20" },
  shortlisted: { label: "Shortlisted",  color: "bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/20" },
  admitted:    { label: "Admitted",     color: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20" },
  rejected:    { label: "Rejected",     color: "bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/20" },
  withdrawn:   { label: "Withdrawn",    color: "bg-zinc-500/10 text-zinc-600 dark:text-zinc-300 border-zinc-500/20" },
};

export default function Admissions() {
  const { user } = useAuth();
  const isSuper = user?.roles?.includes("super_admin");
  const navigate = useNavigate();

  const [tenantId, setTenantId] = useState(isSuper ? "" : user?.tenant_id || "");
  const [institutionId, setInstitutionId] = useState("");
  const [search, setSearch] = useState("");
  const [statusTab, setStatusTab] = useState("all");
  const [copied, setCopied] = useState(false);

  const tenantsQuery = useQuery({
    queryKey: ["tenants"], queryFn: () => TenantsAPI.list(), enabled: isSuper,
  });
  useEffect(() => {
    if (isSuper && !tenantId && tenantsQuery.data?.length) setTenantId(tenantsQuery.data[0].id);
  }, [isSuper, tenantId, tenantsQuery.data]);
  const tenantParam = isSuper && tenantId ? { tenant_id: tenantId } : undefined;
  const effectiveTenant = isSuper ? tenantId : user?.tenant_id;

  const currentTenant = isSuper
    ? tenantsQuery.data?.find((t) => t.id === tenantId)
    : null;
  const tenantSlug = currentTenant?.slug;

  const institutionsQuery = useQuery({
    queryKey: ["institutions", effectiveTenant],
    queryFn: () => InstitutionsAPI.list(tenantParam),
    enabled: !!effectiveTenant,
  });
  const classesQuery = useQuery({
    queryKey: ["classes-flat", effectiveTenant],
    queryFn: () => ClassesAPI.list(tenantParam),
    enabled: !!effectiveTenant,
  });

  const apps = useQuery({
    queryKey: ["applications", effectiveTenant, institutionId, statusTab, search],
    queryFn: () => AdmissionsAPI.list({
      ...(tenantParam || {}),
      ...(institutionId ? { institution_id: institutionId } : {}),
      ...(statusTab !== "all" ? { status: statusTab } : {}),
      ...(search ? { q: search } : {}),
    }),
    enabled: !!effectiveTenant,
  });

  const publicLink = tenantSlug
    ? `${window.location.origin}/apply/${tenantSlug}`
    : "";

  const copyLink = async () => {
    if (!publicLink) return;
    await navigator.clipboard.writeText(publicLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <AppShell>
      <div className="p-8" data-testid="admissions-page">
        <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
          <div>
<h1 className="font-display font-extrabold tracking-tight text-4xl">Admissions</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              Inbox of applications from your public admissions portal — move
              them through the workflow and admit accepted candidates into the
              student roster in one click.
            </p>
          </div>
          {publicLink && (
            <div className="flex items-end gap-2">
              <div className="text-right">
                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground mb-1">Public form link</div>
                <div className="flex items-center gap-2">
                  <code className="text-xs font-mono px-2 py-1 bg-secondary rounded border" data-testid="public-link">{publicLink}</code>
                  <Button size="icon" variant="outline" onClick={copyLink} data-testid="copy-public-link">
                    {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  </Button>
                  <a href={publicLink} target="_blank" rel="noopener noreferrer">
                    <Button size="icon" variant="outline" data-testid="open-public-link"><ExternalLink className="h-3 w-3" /></Button>
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-5 p-4 bg-card rounded-2xl shadow-sm">
          {isSuper && (
            <div className="space-y-1.5 min-w-[180px]">
              <Label className="text-[10px] font-semibold uppercase tracking-wider">Tenant</Label>
              <Select value={tenantId} onValueChange={(v) => { setTenantId(v); setInstitutionId(""); }}>
                <SelectTrigger data-testid="adm-scope-tenant"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {tenantsQuery.data?.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5 min-w-[180px]">
            <Label className="text-[10px] font-semibold uppercase tracking-wider">Institution</Label>
            <Select value={institutionId || "all"} onValueChange={(v) => setInstitutionId(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="adm-scope-institution"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All institutions</SelectItem>
                {institutionsQuery.data?.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 flex-1 min-w-[220px]">
            <Label className="text-[10px] font-semibold uppercase tracking-wider">Search</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8" placeholder="Name or guardian email…" data-testid="adm-search" />
            </div>
          </div>
        </div>

        {/* Status tabs (counts from API) */}
        <Tabs value={statusTab} onValueChange={setStatusTab}>
          <TabsList data-testid="adm-status-tabs">
            <TabsTrigger value="all" data-testid="adm-tab-all">All</TabsTrigger>
            {APPLICATION_STATUSES.map((s) => (
              <TabsTrigger key={s} value={s} data-testid={`adm-tab-${s}`}>
                <span className="capitalize">{STATUS_META[s].label}</span>
                <span className="ml-1.5 font-mono text-[10px] text-muted-foreground">
                  {apps.data?.counts?.[s] ?? 0}
                </span>
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value={statusTab} className="mt-5">
            <div className="bg-card rounded-2xl shadow-sm overflow-hidden">
              {apps.isLoading ? (
                <div className="py-12 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
              ) : !apps.data?.applications?.length ? (
                <div className="py-16 text-center" data-testid="admissions-empty">
                  <Inbox className="h-8 w-8 mx-auto text-muted-foreground/40 mb-2" />
                  <p className="text-sm font-medium">No applications</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Share your public form link or import a CSV.
                  </p>
                </div>
              ) : (
                <table className="w-full" data-testid="admissions-table">
                  <thead className="bg-secondary/40 border-b border-border">
                    <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                      <th className="px-4 py-3">Applicant</th>
                      <th className="px-4 py-3">Guardian</th>
                      <th className="px-4 py-3">Target</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Applied</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {apps.data.applications.map((a) => {
                      const inst = institutionsQuery.data?.find((i) => i.id === a.institution_id);
                      const cls = classesQuery.data?.find((c) => c.id === a.target_class_id);
                      const meta = STATUS_META[a.status] || STATUS_META.applied;
                      return (
                        <tr key={a.id} className="border-b border-border last:border-b-0 hover:bg-secondary/30 transition" data-testid={`adm-row-${a.id}`}>
                          <td className="px-4 py-3">
                            <div className="font-medium">{a.first_name} {a.last_name}</div>
                            <div className="text-xs text-muted-foreground">{a.dob || ""} {a.gender ? `· ${a.gender}` : ""}</div>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div>{a.guardian_name}</div>
                            <div className="text-xs text-muted-foreground font-mono">{a.guardian_email}</div>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {inst?.name || "—"}
                            <div className="text-xs text-muted-foreground">{cls?.name || ""}</div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={meta.color + " capitalize border"}>{meta.label}</Badge>
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground font-mono">
                            {a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button size="sm" variant="ghost" onClick={() => navigate(`/admissions/${a.id}`)} data-testid={`adm-open-${a.id}`}>
                              Open <ArrowUpRight className="h-3 w-3 ml-1" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}
